/**
 * ticketStatusVocabulary — the one shared vocabulary for a Ticket's own `status`, the state of the
 * pilot's core object and the field every ticket surface names.
 *
 * The scale is closed and declared once, on the producer: `app/models/ticket.py:13` fixes
 * `class TicketStatus` as a seven-arm `str, enum.Enum` — unassigned, open, in_progress, waiting,
 * pending, closed, merged — and the column is a real `SAEnum(TicketStatus)`, so every value the
 * wire can carry is one of those seven English tokens. The backend words it in two places and both
 * agree arm for arm: `routers/tickets.py:96` `STATUS_HE` (the map behind `_status_text`, which
 * builds the `status_changed` audit rows this product already renders as Hebrew) and
 * `services/ticket_notifications.py:84` `_status_label` (the map behind a notification body). Those
 * two are the authority this module mirrors, and its test parses both as text and asserts they
 * agree with it value for value.
 *
 * The wording, unlike the scale, was declared eleven more times on the frontend and disagreed with
 * itself — and four of the eleven were short of the scale:
 *
 *   - `services/api.js` held the map **twice, in one file, disagreeing about one stored value**:
 *     `:150` said `waiting` -> "ממתין לתגובה" and `:190` said "המתנה", which collides with that
 *     same map's `pending`. The backend's own comment on this arm (`tickets.py:101`) records the
 *     identical bug being fixed once already: `"המתנה" alone read "from המתנה to המתנה"`.
 *   - `pages/MaintenancePage.jsx` and `pages/ProjectsPage.jsx` each keyed the *ticket* rows they
 *     list through a five-arm map, and neither carried `merged` or `unassigned` — so a ticket
 *     merged into another, or one sitting in the status the backend's SLA and "still open" rules
 *     both special-case, printed the producer's own English token inside a Hebrew queue.
 *   - `pages/Dashboard.jsx` carried six arms (no `unassigned`) behind a `|| t.status` fallback, so
 *     an unassigned ticket's row in the dashboard queue printed `unassigned`.
 *   - `pages/TicketsPage.jsx` carried six arms (no `unassigned`) as the option list of the row's
 *     own status control, which is a worse failure than a mis-worded label: the control's value
 *     had no matching option, so **every unassigned ticket's status cell rendered blank** — the
 *     state a technician sees on a ticket nobody has claimed yet, which is the pilot's first look
 *     at an inbound ticket. `TicketDetail` had already fixed exactly this for its own badge, and
 *     its comment names the arm; the list page kept the gap.
 *   - `pages/CustomerProfilePage.jsx` carried the ticket scale with four arms written in the
 *     *queue* scale's words (`unassigned` "לא הוקצה", `open` "פתוח", `closed` "סגור", `merged`
 *     "מוזג" — the maintenance/project vocabulary this same app uses for a different producer) plus
 *     one key `TicketStatus` cannot mint at all (`pending_customer`), which is how a map that has
 *     every arm still lets a real token fall through: the key set over-claims the scale.
 *   - `pages/GuardianCenterPage.jsx` worded `unassigned` "ממתינה לשיוך" against the ticket
 *     screen's "לא משויכת" — two Hebrew words for one status, the exact complaint `TicketDetail`'s
 *     comment records having already settled the other way.
 *   - `components/ApprovalDecisionBrief.jsx` worded three arms its own way (`waiting` "ממתינה
 *     ללקוח", `pending` "בהמתנה", `merged` "מוזגה לקריאה אחרת") on the card an approver reads
 *     before authorising a state-changing action, and `pages/AgentRunsDashboardPage.jsx` held a
 *     fifth inline copy in its run-list filter (`waiting` "ממתינה", `pending` "בהמתנה"). That brief
 *     also keeps a sentence in its terminal-state warning that phrases the same two states
 *     ("הקריאה כבר סגורה" / "מוזגה לקריאה אחרת"); that is prose about the postcondition, not this
 *     label, and it stays as it is.
 *
 * Wording: the producer's. `לא משויכת` / `פתוחה` / `בטיפול` / `ממתין לתגובה` / `המתנה` / `סגורה` /
 * `מאוחד` — what `routers/tickets.py`, `ticket_notifications._status_label`,
 * `TicketDetail.STATUS_LABELS` and `workEvidenceModel.TICKET_STATUS_HE` all already said. The
 * surfaces that change words are the four that disagreed (`CustomerProfilePage`, `GuardianCenter`
 * and the two inline filters/briefs), and every one of them gains the same word for the same
 * stored value rather than a new one.
 *
 * What this module deliberately does **not** cover: the project / maintenance-item `status` scale.
 * `ProjectsPage.STATUS_HE`, `ProjectDetail.STATUS_HE`, `MaintenancePage.MAINT_STATUS_HE` and
 * `MaintenanceDetail.STATUS_HE` are keyed on a *different* producer (`projects.py` / `maintenance.py`
 * plain strings) with different arms (`done`, `cancelled`), and `Dashboard.QUEUE_STATUS_HE` words
 * those queue rows. They share the word "status" and nothing else; unifying them here would put a
 * ticket's words on a maintenance item, which is the failure this module exists to remove.
 */

export const TICKET_STATUS_HE = {
  unassigned: "לא משויכת",
  open: "פתוחה",
  in_progress: "בטיפול",
  waiting: "ממתין לתגובה",
  pending: "המתנה",
  closed: "סגורה",
  merged: "מאוחד",
};

/**
 * A `status` this build does not name. Named rather than echoed, for the same reason
 * `conversationStatusVocabulary` names its own: every value this field can hold is one of seven
 * English identifiers, so a token this map does not hold is a producer change or a row written
 * before the validation existed — and printing it would put an English word inside a Hebrew line,
 * which is the defect four of the deleted copies had live.
 *
 * A MISSING value reads as this arm too. That is deliberate: a ticket row with no status at all is
 * not something any producer here mints, and a blank cell is what a `<select>` with no matching
 * option already does by accident.
 */
export const TICKET_STATUS_UNKNOWN_HE = "סטטוס קריאה לא מזוהה";

/** The scale in the producer's own declaration order (`class TicketStatus`). */
export const TICKET_STATUS_ORDER = [
  "unassigned",
  "open",
  "in_progress",
  "waiting",
  "pending",
  "closed",
  "merged",
];

/**
 * The arms a technician may set by hand on a ticket — `merged` is written by the merge endpoint
 * (against `merged_into_id`) and `unassigned` by clearing the assigned technician, so neither is a
 * manual target on any surface. `TicketDetail`'s own status-change row states this rule in a
 * comment and derives its buttons the same way; this is that set, once.
 */
export const TICKET_STATUS_EDIT_ORDER = ["open", "in_progress", "pending", "waiting", "closed"];

const has = (map, value) => Object.prototype.hasOwnProperty.call(map, value);

/**
 * The Hebrew wording for a ticket `status`.
 *
 * Resolved with `hasOwnProperty` rather than a bare lookup: a bare `TICKET_STATUS_HE[value]`
 * answers an inherited key (`constructor`, `toString`) with a function, which a `|| fallback`
 * treats as a hit and renders.
 */
export function ticketStatusLabel(value) {
  return has(TICKET_STATUS_HE, value) ? TICKET_STATUS_HE[value] : TICKET_STATUS_UNKNOWN_HE;
}

/**
 * The option list for a control that edits one ticket's status.
 *
 * The five manual targets, plus — when the ticket's current status is outside that set — one
 * DISABLED arm carrying it. The disabled arm is the whole point: a `merged` or `unassigned` row
 * used to be handed a list that could not describe it, and an HTML `<select>` whose value matches
 * no option renders blank, so the ticket list showed nothing at all for a ticket nobody had claimed
 * yet. Disabled rather than selectable because neither arm is a manual target — this narrows the
 * list page to the same five targets `TicketDetail` already offered, instead of leaving `merged` a
 * settable status on one surface and not the other.
 *
 * The labels come from the map rather than being written again, so a picker and a badge cannot
 * drift.
 *
 * Only a `null`/`undefined` value adds no arm: React writes `value={null}` as no attribute at all,
 * so such an option's DOM value would become its own label. Every other value outside the five —
 * including the empty string — gets an arm, because an HTML `<select>` whose value matches no
 * option deselects every option and renders BLANK, which is the failure this replaced.
 */
export function ticketStatusSelectOptions(currentValue) {
  const editable = TICKET_STATUS_EDIT_ORDER.map((value) => ({ value, label: TICKET_STATUS_HE[value] }));
  if (currentValue == null || editable.some((o) => o.value === currentValue)) {
    return editable;
  }
  return [...editable, { value: currentValue, label: ticketStatusLabel(currentValue), disabled: true }];
}
